import { Fragment, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import FormBase from '@/components/forms/new/FormBase';
import { TriggerFormProvider } from './TriggerFormContext.jsx';
import { useTriggerForm } from './useTriggerForm.js';
import { shouldBlockOnJsonBody } from './triggerFormUtils.js';
import { addSpacing } from '../../../../utils/utils';
import walkthroughIds from '../walkthroughIds';
import { useDisclosure } from '@chakra-ui/react';
import { LoadingIndicator } from '@/components/v4';

import TriggerDeleteModal from './modals/TriggerDeleteModal';
import TriggerBasicInfo from './TriggerBasicInfo';
import TriggerEventSection from './TriggerEventSection';
import TriggerActionSection from './TriggerActionSection';
import useCreateTrigger from './queries/useCreateTrigger.jsx';
import useGetTriggerByID from './queries/useGetTriggerByID.jsx';
import useUpdateTrigger from './queries/useUpdateTrigger.jsx';
import useGetTriggerEvents from './queries/useGetTriggerEvents.jsx';

const FormStateSync = ({ errors, values, onErrorsChange, onValuesChange }) => {
    useEffect(() => {
        onErrorsChange(errors);
    }, [errors, onErrorsChange]);

    useEffect(() => {
        onValuesChange(values);
    }, [values, onValuesChange]);

    return null;
};

const TriggersEditContent = ({ mode, resetToMainTable, selectedTriggerID }) => {
    const { darkMode } = useSelector((state) => state.settings);
    const { editMode } = walkthroughIds;

    const { currentScope } = useSelector((state) => state.login);
    const { currentCompany, currentPartner, currentWholesaler } = useSelector(
        (state) => state.navigationLists,
    );

    const {
        isOpen: isDeleteOpen,
        onOpen: onDeleteOpen,
        onClose: onDeleteClose,
    } = useDisclosure();

    const {
        formData,
        setFormData,
        updateFormData,
        headers,
        setHeaders,
        setMode,
    } = useTriggerForm();

    const [cursorPosition, setCursorPosition] = useState(0);
    const [triggerEvents, setTriggerEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [initialFormData, setInitialFormData] = useState(null);
    const [currentErrors, setCurrentErrors] = useState({});
    const [currentValues, setCurrentValues] = useState({});

    const isAddMode = mode === 'Add';
    const isEditMode = mode === 'Edit';

    const getTriggerTypeFromScope = (scope) => {
        switch (scope) {
            case 20:
                return 'Company';
            case 40:
                return 'Partner';
            case 60:
                return 'Wholesaler';
            default:
                return 'Global';
        }
    };

    const triggerTypeToScope = (type) => {
        switch (type) {
            case 'Company':
                return 20;
            case 'Partner':
                return 40;
            case 'Wholesaler':
                return 60;
            case 'Global':
                return 80;
            default:
                return undefined;
        }
    };

    const { isLoading: isEventsLoading } = useGetTriggerEvents(
        setTriggerEvents,
        triggerTypeToScope(formData?.triggerType),
    );

    useEffect(() => {
        setMode(mode);
    }, [mode, setMode]);

    useEffect(() => {
        if (isAddMode) {
            const triggerType = getTriggerTypeFromScope(currentScope);
            const newFormData = {
                name: '',
                description: '',
                enabled: true,
                triggerType: triggerType,
                eventID: '',
                action: {
                    actionType: 'Email',
                    emailAddress: null,
                    subject: '',
                    body: '',
                    htmlBody: false,
                    httpMethod: 'POST',
                    uri: '',
                    headers: null,
                },
                companyID: currentCompany,
                partnerID: currentPartner,
                wholesalerID: currentWholesaler,
                isDeleted: false,
                latestFiredAt: null,
                maximumScope: currentScope,
            };
            setFormData(newFormData);
            setInitialFormData(newFormData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { isLoading, isFetching } = useGetTriggerByID(
        selectedTriggerID,
        (data) => {
            setFormData(data);
            if (isEditMode && !initialFormData) {
                setInitialFormData(data);
            }
        },
        resetToMainTable,
        isEditMode,
    );

    useEffect(() => {
        if (formData.eventID && triggerEvents.length > 0) {
            const event = triggerEvents.find((e) => e.id === formData.eventID);
            setSelectedEvent(event);
        }
    }, [formData.eventID, triggerEvents]);

    const fields = useMemo(() => {
        if (!selectedEvent?.fields) return [];

        return selectedEvent.fields.split(',').map((v) => ({
            id: '[' + v.trim() + ']',
            name: addSpacing(v.trim()),
        }));
    }, [selectedEvent?.fields]);

    const getInitialValues = () => {
        return formData;
    };

    const hasFormErrors = (errors) => {
        if (!errors || typeof errors !== 'object') return false;

        const checkNestedErrors = (obj) => {
            return Object.values(obj).some((value) => {
                if (typeof value === 'object' && value !== null) {
                    return checkNestedErrors(value);
                }
                return Boolean(value);
            });
        };

        return checkNestedErrors(errors);
    };

    const hasFormChanged = (currentValues) => {
        if (!initialFormData || !currentValues) return false;

        const normalizeFormData = (data) => {
            if (!data || typeof data !== 'object') return {};

            return {
                ...data,
                action: {
                    ...(data.action || {}),
                    headers: data.action?.headers || null,
                },
            };
        };

        const normalizedInitial = normalizeFormData(initialFormData);
        const normalizedCurrent = normalizeFormData(currentValues);

        return !isEqual(normalizedInitial, normalizedCurrent);
    };

    const calculateIsSubmitDisabled = () => {
        if (!currentErrors || !currentValues) return true;

        // Formik's own errors.body is left out of the gate because
        // shouldBlockOnJsonBody reports the same check, and only for a body the
        // user actually edited.
        const { body: _jsonBodyError, ...blockingErrors } = currentErrors;

        const hasErrors =
            hasFormErrors(blockingErrors) ||
            shouldBlockOnJsonBody(formData, initialFormData);
        const hasChanged = hasFormChanged(currentValues);

        return hasErrors || !hasChanged;
    };

    const breadCrumbTrail = [
        { label: 'Triggers', path: resetToMainTable },
        {
            label: isAddMode ? 'Add Trigger' : 'Edit Trigger',
            path: '',
        },
    ];

    const header = isAddMode ? 'Add Trigger' : 'Edit Trigger';

    const handleEventChange = (eventID) => {
        const event = triggerEvents.find((e) => e.id === eventID);
        setSelectedEvent(event);
        updateFormData('eventID', eventID);
    };

    const updateActionHeaders = (newHeaders) => {
        setHeaders(newHeaders);
    };

    const { mutate: createTrigger } = useCreateTrigger(
        formData,
        isAddMode,
        resetToMainTable,
    );
    const { mutate: updateTrigger } = useUpdateTrigger(
        formData,
        isEditMode,
        resetToMainTable,
    );

    const finalFormSubmit = () => {
        const headerData = {};
        if (Array.isArray(headers) && headers.length > 0) {
            headers.forEach((header) => {
                if (header.key && header.value) {
                    headerData[header.key] = header.value;
                }
            });
        }

        const getEmailAddressString = (emailAddress) => {
            if (!emailAddress) return null;
            if (Array.isArray(emailAddress)) {
                return emailAddress.join(',');
            }
            if (typeof emailAddress === 'string') {
                return emailAddress;
            }
            return String(emailAddress);
        };

        const actionData =
            formData.action.actionType === 'Email'
                ? {
                      ...formData.action,
                      headers: null,
                      httpMethod: null,
                      uri: null,
                      emailAddress: getEmailAddressString(
                          formData.action?.emailAddress,
                      ),
                  }
                : {
                      ...formData.action,
                      headers: headerData,
                      subject: null,
                      emailAddress: null,
                      htmlBody: false,
                  };

        const submitData = isAddMode
            ? {
                  ...formData,
                  companyID:
                      formData.companyID === ''
                          ? currentCompany
                          : formData.companyID,
                  partnerID:
                      formData.partnerID === ''
                          ? currentPartner
                          : formData.partnerID,
                  wholesalerID:
                      formData.wholesalerID === ''
                          ? currentWholesaler
                          : formData.wholesalerID,
                  action: actionData,
                  maximumScope: currentScope,
              }
            : {
                  ...formData,
                  action: actionData,
              };

        setFormData(submitData);

        isAddMode ? createTrigger() : updateTrigger();
    };

    const formFields = (errors, touched, values) => {
        const fieldComponents = [
            <Fragment key="basic-info">
                <FormStateSync
                    errors={errors}
                    values={values}
                    onErrorsChange={setCurrentErrors}
                    onValuesChange={setCurrentValues}
                />
                <TriggerBasicInfo
                    darkMode={darkMode}
                    errors={errors}
                    touched={touched}
                />
            </Fragment>,

            <TriggerEventSection
                key="event-section"
                darkMode={darkMode}
                eventEnum={triggerEvents}
                isEventsLoading={isEventsLoading}
                selectedEvent={selectedEvent}
                onEventChange={handleEventChange}
                errors={errors}
                touched={touched}
                values={values}
                updateFormData={updateFormData}
            />,
            formData?.eventID && (
                <TriggerActionSection
                    key="action-section"
                    darkMode={darkMode}
                    fields={fields}
                    cursorPosition={cursorPosition}
                    setCursorPosition={setCursorPosition}
                    errors={errors}
                    touched={touched}
                    values={values}
                    updateFormData={updateFormData}
                    actionHeaders={formData?.action?.headers || []}
                    updateActionHeaders={updateActionHeaders}
                />
            ),
        ].filter(Boolean);

        return fieldComponents;
    };

    if ((isLoading || isFetching) && isEditMode) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <FormBase
                breadCrumbTrail={breadCrumbTrail}
                header={header}
                initialValues={getInitialValues()}
                finalFormSubmit={finalFormSubmit}
                backLink={resetToMainTable}
                isSubmitDisabled={calculateIsSubmitDisabled()}
                ufBackBtn={editMode.ufBackBtn}
                ufSaveBtn={editMode.ufSaveBtn}
                deleteButton={
                    isEditMode && {
                        isLoading: isLoading,
                        onDeleteOpen: onDeleteOpen,
                        dataWalkthroughId: editMode.ufDeleteBtn,
                    }
                }
                enableReinitialize={true}>
                {({ errors, touched, values }) =>
                    formFields(errors, touched, values)
                }
            </FormBase>
            <TriggerDeleteModal
                isOpen={isDeleteOpen}
                onClose={onDeleteClose}
                triggerData={formData}
                resetToMainTable={resetToMainTable}
            />
        </>
    );
};

// Main component that provides the context
const TriggersEdit = ({ mode, resetToMainTable, selectedTriggerID }) => {
    const { currentCompany, currentPartner, currentWholesaler } = useSelector(
        (state) => state.navigationLists,
    );

    const initialData = {
        companyID: currentCompany,
        partnerID: currentPartner,
        wholesalerID: currentWholesaler,
    };

    return (
        <TriggerFormProvider initialData={initialData}>
            <TriggersEditContent
                mode={mode}
                resetToMainTable={resetToMainTable}
                selectedTriggerID={selectedTriggerID}
                initialData={initialData}
            />
        </TriggerFormProvider>
    );
};

export default TriggersEdit;
